<template>
  <div
    class="artwork-quantity"
    :class="{
      'artwork-quantity--with-border': showBorderUI,
      'artwork-quantity--with-border--invalid': showBorderUI && isMinQuantityInvalid
    }"
  >
    <div
      v-if="!printAsIs && !isJerseyNumbers && !isLetters && !showBorderUI"
      class="artwork-quantity__title"
    >
      Quantity
    </div>
    <StepNumber
      v-if="showBorderUI"
      :step-number="isPuff ? 4 : 3"
      content="Add Quantity"
    ></StepNumber>

    <div
      class="artwork-quantity__row"
      :class="{
        'artwork-quantity__row--gang': printAsIs,
        'artwork-quantity__row--error': isMinQuantityInvalid
      }"
    >
      <div v-if="(isJerseyNumbers || isLetters) && quantity" class="artwork-quantity__value">
        <div class="artwork-quantity__label">Quantity</div>
        <div class="artwork-quantity__jersey-numbers-total">{{ quantity }}</div>
      </div>

      <div
        class="artwork-quantity__prices"
        :class="{ hidden: printAsIs || isJerseyNumbers || isLetters }"
      >
        <div v-if="isJerseyNumbers || isLetters" class="artwork-quantity__label">Unit Price</div>
        <div class="artwork-quantity__retail-price">{{ n(retailPrice, "currency") }}</div>
        <div class="artwork-quantity__actual-price">{{ n(unitPrice, "currency") }}</div>
      </div>

      <div v-if="!isJerseyNumbers && !isLetters" class="artwork-quantity__input-wrapper">
        <div v-if="printAsIs" class="artwork-quantity__title-wrapper">
          <label :for="id" class="artwork-quantity__title">Quantity</label>
        </div>
        <input
          :id="id"
          :class="{
            'artwork-quantity__input js-quantity-form-input': true,
            'artwork-quantity__input--invalid': isMinQuantityInvalid
          }"
          maxlength="4"
          type="tel"
          :name="id"
          autocomplete="off"
          data-hj-allow
          :data-variant-id="variantId"
          :value="quantity"
          :disabled="disabled"
          @input="handleInput"
          @blur="handleInputBlur"
          @keyup.enter="submitForm"
        />
        <UseSvg
          v-if="outOfStock"
          class="artwork-quantity__icon-cross"
          icon="cross"
          width="12"
          height="12"
        />
      </div>

      <span v-if="outOfStock" class="artwork-quantity__out-of-stock-notice">Out of stock</span>

      <div v-if="printAsIs" class="artwork-quantity__total-section">
        <div v-if="quantity > 0 && total" class="artwork-quantity__total">
          <div class="artwork-quantity__total-wrapper">
            <div v-if="showRetailPrice" class="artwork-quantity__total-old">
              {{ n(retailTotal, "currency") }}
            </div>
            <div class="artwork-quantity__total-price">
              <span class="artwork-quantity__total-inline-title">Total:</span>
              {{ n(total, "currency") }}
            </div>
          </div>
        </div>
        <div v-if="quantity > 0 && percentage > 0" class="artwork-quantity__off">
          {{ percentage }}% off
        </div>
      </div>

      <div
        v-if="!printAsIs && quantity > 0 && total"
        :class="{
          'artwork-quantity__total': true,
          'artwork-quantity__total--mobile': isMobile
        }"
      >
        <div
          :class="{
            'artwork-quantity__total-title': !isJerseyNumbers && !isLetters,
            'artwork-quantity__label': isJerseyNumbers || isLetters
          }"
        >
          Total
        </div>
        <div class="artwork-quantity__total-wrapper">
          <div v-if="showRetailPrice" class="artwork-quantity__total-old">
            {{ n(retailTotal, "currency") }}
          </div>
          <div class="artwork-quantity__total-price">{{ n(total, "currency") }}</div>
        </div>
      </div>
      <div v-if="!printAsIs && quantity > 0 && percentage > 0" class="artwork-quantity__off">
        {{ percentage }}% off
      </div>

      <ArtworkAlert v-if="isMinQuantityInvalid" class="artwork-quantity__error">
        Minimum quantity of {{ minStickerQuantity }} per design
      </ArtworkAlert>

      <ArtworkShippingProgress v-if="showShippingProgress" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import isEqual from "lodash.isequal";
import ArtworkAlert from "components/jiffy/vue-product-details-dtf/ArtworkAlert.vue";
import ArtworkShippingProgress from "./ArtworkShippingProgress.vue";
import StepNumber from "./common/StepNumber.vue";
import { n } from "../vue-cart/helpers/currency";
import UseSvg from "../vue-cart/UseSvg.vue";
import { useDtfStore } from "./store";

export default {
  name: "ArtworkQuantity",
  components: { ArtworkShippingProgress, UseSvg, ArtworkAlert, StepNumber },
  props: {
    index: {
      type: Number,
      default: 0
    },
    artwork: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showFreeShipping: {
      type: Boolean,
      default: false
    },
    isMultiUpload: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return { n };
  },
  data() {
    return {
      debounceTimeout: null,
      previousData: {
        quantity: null
      }
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "modalArtworks",
      "outOfStock",
      "printAsIs",
      "simulation",
      "sticker",
      "isPuff",
      "variantId",
      "minStickerQuantity",
      "isV15",
      "isMobile",
      "isJerseyNumbers",
      "isLetters",
      "letterNumberArtwork",
      "summary",
      "showStepsUI"
    ]),
    quantity() {
      if (this.isMobile) {
        return this.artwork?.quantity || 0;
      }
      return this.artwork?.quantity;
    },
    pricing() {
      if (this.isJerseyNumbers || this.isLetters) {
        return this.letterNumberArtwork;
      }
      if (this.artwork?.isFile) {
        return this.simulation;
      }
      return this.artwork?.pricing || this.simulation;
    },
    percentage() {
      if (this.pricing?.percentage) {
        return Number(this.pricing?.percentage).toFixed(0);
      }
      return null;
    },
    retailPrice() {
      if (this.pricing?.retailPrice) {
        return Number(this.pricing.retailPrice).toFixed(2);
      }
      return null;
    },
    unitPrice() {
      if (this.pricing?.unitPrice) {
        return Number(this.pricing.unitPrice).toFixed(2);
      }
      return null;
    },
    id() {
      return `artwork[${this.index}][variants][${this.variantId}][quantity]`;
    },
    retailTotal() {
      return (this.retailPrice * this.quantity).toFixed(2);
    },
    total() {
      const value = this.unitPrice * this.quantity;
      if (value > 0) {
        return value.toFixed(2);
      }
      return null;
    },
    showRetailPrice() {
      return this.retailTotal !== this.total;
    },
    isMinQuantityInvalid() {
      if (this.sticker || this.isPuff) {
        return this.quantity && this.quantity < this.minStickerQuantity;
      }

      return false;
    },
    showShippingProgress() {
      if ((this.isJerseyNumbers || this.isLetters) && this.quantity && this.showFreeShipping) {
        return true;
      }

      return (
        !this.printAsIs &&
        this.quantity &&
        this.showFreeShipping &&
        this.artwork &&
        !this.artwork.isSimulated &&
        !this.artwork.isLoading
      );
    },
    showBorderUI() {
      return this.showStepsUI && !this.isMultiUpload;
    }
  },
  watch: {
    showShippingProgress() {
      this.updatePdpDates();
    }
  },
  methods: {
    ...mapActions(useDtfStore, ["updateArtworkValues"]),
    ...mapActions(useDtfStore, ["getArtworksPricing", "submitForm", "updatePdpDates"]),
    allowNumbersOnly(e) {
      const { value } = e.target;
      const quantity = value.replace(/[^0-9]/g, "").replace(/^0/, "");
      e.target.value = quantity;

      this.updateArtworkValues({ index: this.artwork.originalIndex, quantity });
    },
    handleInput(e) {
      this.allowNumbersOnly(e);
      this.debouncedHandleInputBlur();
    },
    debouncedHandleInputBlur() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.handleInputBlur();
      }, 1000); // 1 second debounce time
    },
    handleInputBlur() {
      const currentData = {
        quantity: this.quantity
      };

      if (isEqual(currentData, this.previousData)) {
        return;
      }

      this.previousData = currentData;
      this.getArtworksPricing();
      this.updatePdpDates();
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.artwork-quantity {
  $this: &;

  padding-bottom: 16px;

  &--with-border {
    border: 1px solid $gray-border;
    padding: 10px 15px;
    border-radius: 8px;
    box-shadow: 0px 25px 30px 0px rgba($black, 0.05);
    margin-bottom: 16px;
    background-color: $white;

    .step-number {
      margin-bottom: 10px;
    }

    #{$this}__row {
      padding-bottom: 10px;
    }
  }

  &--with-border--invalid {
    border-color: $red-border;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
  }

  &__row {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;

    @include with-large-desktop {
      flex-wrap: nowrap;
    }

    &--error {
      position: relative;

      @include with-large-desktop {
        margin-bottom: 69px;
      }
    }

    &--gang {
      background-color: $section-grey;
      border: 1px solid $section-grey-border;
      padding: 8px 8px 8px 16px;
      border-radius: 3px;
      justify-content: space-between;
      align-items: center;

      @include only-tablet {
        padding-left: 8px;
      }

      #{$this}__title {
        margin-right: 6px;
      }

      #{$this}__input-wrapper {
        margin-left: 0;
        display: flex;
        align-items: center;
      }

      #{$this}__total-wrapper {
        padding-right: 0;
        padding-top: 5px;

        @include only-mobile {
          width: 122px;
        }
      }

      #{$this}__total {
        @include only-tablet {
          margin-left: 0;
        }
      }

      #{$this}__off {
        background-color: $feedback-success-darker;
        color: $white;
        font-size: 12px;
        padding: 2px 4px;
        border-radius: 4px;
        flex: unset;
      }
    }
  }

  &__total-section {
    display: flex;
    align-items: flex-end;
  }

  &__total-inline-title {
    margin-right: 8px;
    font-size: 16px;

    @include only-tablet {
      margin-right: 0;
    }
  }

  &__retail-price {
    font-size: 12px;
    text-decoration: line-through;
    color: $warm-grey;
    line-height: 1;
    margin-bottom: 6px;
  }

  &__actual-price {
    font-weight: 700;
    line-height: 1;
    text-align: right;
    min-height: 20px;
    font-size: 18px;
    color: $variant-red;
  }

  &__input {
    display: block;
    box-shadow: none;
    border-radius: 3px;
    border: 1px solid $gray-label;
    outline: none;
    margin: 0;
    background: $white;
    height: 44px;
    width: 42px;
    text-align: center;
    font-size: 16px;
    transition: background-color 0.4s ease-in-out;
    padding: 0;

    &:disabled {
      background: $section-shining-grey;
    }

    &:focus {
      border: 2px solid $black;
    }

    &--invalid {
      &,
      &:focus {
        border-color: $red-light;
      }
    }
  }

  &__prices {
    text-align: right;
    min-width: 60px;
    margin-top: 30px;
    align-self: end;
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
  }

  &__input-wrapper {
    position: relative;
    margin-left: 16px;
  }

  &__icon-cross {
    color: $error;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__total {
    position: relative;
    margin-left: 12px;

    &--mobile {
      #{$this}__total-wrapper {
        padding: 2px 5px;
        text-align: left;
      }

      #{$this}__total-title {
        left: 3px;
        right: auto;
      }
    }
  }

  &__total-title {
    position: absolute;
    top: -22px;
    right: 8px;
    font-size: 12px;
    font-weight: 700;
    line-height: 13px;
  }

  &__total-wrapper {
    padding-top: 2px;
    padding-right: 10px;
    background: $section-grey;
    border-radius: 8px;
    text-align: right;
  }

  &__total-old {
    color: $warm-grey;
    font-size: 12px;
    font-weight: 400;
    text-decoration: line-through;
  }

  &__total-price {
    font-size: 18px;
    font-weight: 700;
    line-height: 20px;
  }

  &__off {
    flex: 1;
    font-size: 18px;
    font-weight: 700;
    line-height: 20px;
    color: $feedback-success-darker;
    display: flex;
    align-items: flex-end;
    padding-bottom: 3px;
    margin-left: 8px;
  }

  &__error {
    width: 100%;
    margin-top: 16px;

    @include with-large-desktop {
      position: absolute;
      top: 100%;
      left: 0;
    }
  }

  &__out-of-stock-notice {
    margin-left: 12px;
    color: $error;
    font-size: 12px;
    font-weight: 400;
  }

  &__value {
    color: $text-black;
    font-size: 18px;
    font-weight: 400;
    line-height: 1.11;
  }

  &__label {
    color: $text-black;
    font-size: 16px;
    font-weight: 700;
    line-height: 0.81;
    margin-bottom: 8px;
  }

  &__jersey-numbers-total {
    display: block;
    box-shadow: none;
    border-radius: 3px;
    border: 1px solid $gray-label;
    outline: none;
    margin: 0;
    background: $section-shining-grey;
    height: 44px;
    width: 42px;
    text-align: center;
    font-size: 16px;
    padding-top: 12px;
  }
}
</style>
